import { templateQuantityInUnit } from '../../review/reviewUtils';
import { AlternativeLinkCommitParent, AlternativeLinkGroup, AlternativeLinkParent, AlternativeLinkOccurrence, AlternativeLinkProduct, AlternativeLinkPlan, AlternativeLinkPreviewRequest } from '../../../../../services/costPrice/alternativeLinkTypes';

export interface AlternativeReviewState {
  plan: AlternativeLinkPlan;
  members: Record<string, string[]>;
  selected: Record<string, string[]>;
  selectedAlternatives?: Record<string, string[]>;
  equivalences: Record<string, Record<string, string>>;
  quantities: Record<string, string>;
  waste: Record<string, string>;
  staleParents: string[];
  staleCandidates: string[];
  appliedParents: string[];
  sentQuestions?: string[];
  /** Unlinked flow: matchDecisionKey -> reviewer verdict on the product match itself. */
  matchDecisions?: Record<string, 'yes' | 'no'>;
}

export const overrideKey = (occurrenceId: string, alternativeKey: string) => JSON.stringify([occurrenceId, alternativeKey]);
const unique = <T,>(values: T[]) => Array.from(new Set(values));
export const sameMembers = (a: string[], b: string[]) => JSON.stringify([...a].sort()) === JSON.stringify([...b].sort());
export function groupReviewPresentation(group: AlternativeLinkGroup, members: string[], previewGroup?: AlternativeLinkGroup) {
  const manualMembership = !sameMembers(members, group.memberKeys) || group.reasonCodes.includes('manual');
  return {
    status: manualMembership ? 'review' : group.status,
    confidence: manualMembership ? null : group.confidence,
    reasonCodes: manualMembership ? unique(['manual', ...(previewGroup?.reasonCodes ?? [])]) : group.reasonCodes,
    connectedProductKeys: previewGroup?.connectedProductKeys ?? group.connectedProductKeys,
  };
}
export function createAlternativeReview(plan: AlternativeLinkPlan): AlternativeReviewState {
  return { plan, members: Object.fromEntries(plan.groups.map(g => [g.id, [...g.memberKeys]])), selected: {}, equivalences: {}, quantities: {}, waste: {}, staleParents: [], staleCandidates: [], appliedParents: [] };
}

/** Display each directed missing link once, even when discovery groups overlap. */
export function alternativeDishReviews(state: AlternativeReviewState) {
  const products = new Map(state.plan.products.map(product => [product.key, product]));
  return state.plan.parents.filter(parent => !state.appliedParents.includes(parent.key)).map(parent => ({
    parent,
    ingredients: parent.occurrences.map(occurrence => {
      const candidates = new Map<string, { product: AlternativeLinkProduct; group: AlternativeLinkGroup }>();
      for (const group of state.plan.groups) {
        const members = state.members[group.id] ?? group.memberKeys;
        if (!members.includes(occurrence.primaryKey)) continue;
        for (const key of members) {
          if (state.plan.selectedProductKeys && !state.plan.selectedProductKeys.includes(key)) continue;
          if (key === occurrence.primaryKey || occurrence.existingAlternatives.some(existing => existing.key === key) || candidates.has(key)) continue;
          const product = products.get(key);
          if (product) candidates.set(key, { product, group });
        }
      }
      return { occurrence, primary: products.get(occurrence.primaryKey), candidates: Array.from(candidates.values()).sort((a, b) =>
        (b.product.purchasesLast60Days?.orderCount ?? 0) - (a.product.purchasesLast60Days?.orderCount ?? 0)) };
    }),
  })).filter(dish => dish.ingredients.some(ingredient => ingredient.candidates.length));
}

export const matchDecisionKey = (selectedKey: string, primaryKey: string) => JSON.stringify([selectedKey, primaryKey]);

/** Unlinked flow: one line per selected product × matched primary ingredient, with every dish/prep using that primary. */
export function alternativeProductMatches(state: AlternativeReviewState) {
  const selectedKeys = state.plan.selectedProductKeys ?? [];
  const products = new Map(state.plan.products.map(product => [product.key, product]));
  const matches: Array<{ id: string; product: AlternativeLinkProduct; primary: AlternativeLinkProduct; group: AlternativeLinkGroup;
    uses: Array<{ parent: AlternativeLinkParent; occurrence: AlternativeLinkOccurrence }> }> = [];
  for (const selectedKey of selectedKeys) {
    const product = products.get(selectedKey);
    if (!product) continue;
    for (const group of state.plan.groups) {
      const members = state.members[group.id] ?? group.memberKeys;
      if (!members.includes(selectedKey)) continue;
      for (const primaryKey of members) {
        const id = matchDecisionKey(selectedKey, primaryKey);
        const primary = products.get(primaryKey);
        if (primaryKey === selectedKey || selectedKeys.includes(primaryKey) || !primary || matches.some(match => match.id === id)) continue;
        // Only configured parent ingredients count; existing alternatives are never a match target.
        const uses = state.plan.parents.filter(parent => !state.appliedParents.includes(parent.key)).flatMap(parent => parent.occurrences
          .filter(occurrence => occurrence.primaryKey === primaryKey && !occurrence.existingAlternatives.some(existing => existing.key === selectedKey))
          .map(occurrence => ({ parent, occurrence })));
        if (uses.length) matches.push({ id, product, primary, group, uses });
      }
    }
  }
  return matches;
}

/** A rejected (or reopened) match never keeps dish selections behind the reviewer's back. */
export function decideProductMatch(state: AlternativeReviewState, match: ReturnType<typeof alternativeProductMatches>[number], decision: 'yes' | 'no' | null): AlternativeReviewState {
  const matchDecisions = { ...state.matchDecisions };
  if (decision) matchDecisions[match.id] = decision; else delete matchDecisions[match.id];
  const cleared = decision === 'yes' ? state : match.uses.reduce((next, use) => selectDishAlternative(next, use.occurrence.id, match.product.key, false), state);
  return { ...cleared, matchDecisions };
}

export function defaultAlternativeQuantity(occurrence: AlternativeLinkOccurrence, product: AlternativeLinkProduct): number | null {
  const unit = product.quantityInfo?.stdUnit;
  if (!unit || /^(unit|units|portion|portions)$/i.test(unit.trim()) || /^(unit|units|portion|portions)$/i.test(occurrence.stdUnit.trim())) return null;
  // Convert a whole unit to get the shared factor, keeping small recipe quantities precise.
  const factor = templateQuantityInUnit(1, occurrence.stdUnit, unit);
  return factor === null ? null : occurrence.quantityNeeded * factor;
}

export function selectDishAlternative(state: AlternativeReviewState, occurrenceId: string, candidateKey: string, checked: boolean): AlternativeReviewState {
  const occurrence = state.plan.parents.flatMap(parent => parent.occurrences).find(item => item.id === occurrenceId);
  if (!occurrence || occurrence.existingAlternatives.some(item => item.key === candidateKey) || occurrence.primaryKey === candidateKey) return state;
  const selectedAlternatives = { ...state.selectedAlternatives, [occurrenceId]: checked
    ? unique([...(state.selectedAlternatives?.[occurrenceId] ?? []), candidateKey])
    : (state.selectedAlternatives?.[occurrenceId] ?? []).filter(key => key !== candidateKey) };
  const selected = { ...state.selected };
  for (const group of state.plan.groups) {
    const members = state.members[group.id] ?? group.memberKeys;
    if (!members.includes(occurrence.primaryKey)) continue;
    const remaining = (selected[group.id] ?? []).filter(id => id !== occurrenceId);
    selected[group.id] = selectedAlternatives[occurrenceId].some(key => members.includes(key)) ? [...remaining, occurrenceId] : remaining;
  }
  return { ...state, selected, selectedAlternatives };
}

/** Keep reviewed snapshots and manual exclusions. New/expanded groups require new consent. */
export function mergeAlternativeScan(state: AlternativeReviewState, incoming: AlternativeLinkPlan): AlternativeReviewState {
  if (!sameMembers(incoming.categories ?? [], state.plan.categories ?? []) || !sameMembers(incoming.selectedProductKeys ?? [], state.plan.selectedProductKeys ?? [])) return state;
  if (incoming.restaurantId !== state.plan.restaurantId || incoming.historyWindow !== state.plan.historyWindow || (incoming.categoryFilter ?? 'all') !== (state.plan.categoryFilter ?? 'all')) return state;
  const parents = new Map(state.plan.parents.map(p => [p.key, p]));
  const products = new Map(state.plan.products.map(p => [p.key, p]));
  const staleParents = [...state.staleParents];
  const staleCandidates = [...state.staleCandidates];
  incoming.parents.forEach(p => {
    const old = parents.get(p.key);
    if (!old) parents.set(p.key, p);
    else if (old.fingerprint !== p.fingerprint && !state.appliedParents.includes(p.key)) staleParents.push(p.key);
  });
  incoming.products.forEach(p => {
    const old = products.get(p.key);
    if (!old) products.set(p.key, p);
    else if (old.fingerprint !== p.fingerprint) staleCandidates.push(p.key);
  });
  const groups = state.plan.groups.map(group => ({ ...group }));
  const members = { ...state.members };
  incoming.groups.forEach(g => {
    const old = groups.find(previous => sameMembers(previous.memberKeys, g.memberKeys));
    if (old) {
      // Parent conflicts above block the old choices; unchanged newly found rows stay unchecked.
      old.occurrenceIds = unique([...old.occurrenceIds, ...g.occurrenceIds]);
    } else if (!groups.some(previous => previous.id === g.id)) {
      groups.push(g);
      members[g.id] = [...g.memberKeys];
    }
  });
  const scannedSourceKeys = unique([...state.plan.scannedSourceKeys, ...incoming.scannedSourceKeys]);
  // A selection skipped on a later batch (e.g. linked meanwhile) must not stay "remaining".
  const skippedProducts = [...(state.plan.skippedProducts ?? []), ...(incoming.skippedProducts ?? [])]
    .filter((skipped, index, all) => all.findIndex(other => other.key === skipped.key) === index);
  const remainingSourceKeys = unique([...state.plan.remainingSourceKeys, ...incoming.remainingSourceKeys])
    .filter(key => !scannedSourceKeys.includes(key) && !skippedProducts.some(skipped => skipped.key === key));
  return { ...state, members, staleParents: unique(staleParents), staleCandidates: unique(staleCandidates), plan: {
    ...state.plan, parents: Array.from(parents.values()), products: Array.from(products.values()), groups,
    scannedSourceKeys, remainingSourceKeys, ...(state.plan.selectedProductKeys ? { skippedProducts } : {}), warnings: unique([...state.plan.warnings, ...incoming.warnings]),
    summary: { ...incoming.summary, scannedSources: scannedSourceKeys.length, remainingSources: remainingSourceKeys.length, groups: groups.length, incomplete: state.plan.summary.incomplete + incoming.summary.incomplete },
  } };
}

export function groupOccurrences(state: AlternativeReviewState, group: AlternativeLinkGroup) {
  const members = state.members[group.id] ?? group.memberKeys;
  return state.plan.parents.filter(p => !state.appliedParents.includes(p.key)).flatMap(parent => {
    const matches = parent.occurrences.filter(o => members.includes(o.primaryKey));
    const multiPrimary = new Set(matches.map(o => o.primaryKey)).size > 1;
    return matches.map(occurrence => ({ parent, occurrence, requiresReview: multiPrimary || occurrence.issues.length > 0 || parent.indirectUses.length > 0 }));
  });
}

export function selectSuggestedGroups(state: AlternativeReviewState): AlternativeReviewState {
  const selected = { ...state.selected };
  state.plan.groups.filter(g => g.status === 'suggested' && sameMembers(state.members[g.id], g.memberKeys) && !g.memberKeys.some(k => state.staleCandidates.includes(k))).forEach(g => {
    // An explicit exclusion must survive repeated bulk selection.
    if (selected[g.id] !== undefined) return;
    selected[g.id] = groupOccurrences(state, g).filter(row => !row.requiresReview && !state.staleParents.includes(row.parent.key)).map(row => row.occurrence.id);
  });
  return { ...state, selected };
}

export function editGroupMembers(state: AlternativeReviewState, groupId: string, members: string[]): AlternativeReviewState {
  const group = state.plan.groups.find(g => g.id === groupId);
  const occurrences = group ? groupOccurrences(state, group).map(row => row.occurrence.id) : [];
  const keep = (key: string) => !occurrences.includes((JSON.parse(key) as string[])[0]);
  return { ...state, members: { ...state.members, [groupId]: members }, selected: { ...state.selected, [groupId]: [] }, equivalences: { ...state.equivalences, [groupId]: {} }, quantities: Object.fromEntries(Object.entries(state.quantities).filter(([key]) => keep(key))), waste: Object.fromEntries(Object.entries(state.waste).filter(([key]) => keep(key))) };
}

/** Validate raw input before JSON serialization can turn NaN into null. Blank override = automatic. */
export function buildAlternativePreviewRequest(state: AlternativeReviewState): { request: AlternativeLinkPreviewRequest; invalidInput: boolean } {
  let invalidInput = false;
  const parse = (raw: string, waste = false): number => {
    const value = Number(raw);
    if (!Number.isFinite(value) || (waste ? value < 0 || value > 100 : value <= 0)) invalidInput = true;
    return value;
  };
  const groups = state.plan.groups.map(g => ({ id: g.id, memberKeys: state.members[g.id] ?? g.memberKeys })).filter(g => g.memberKeys.length >= 2);
  const selectedOccurrences = Object.fromEntries(groups.map(g => [g.id, (state.selected[g.id] ?? []).filter(id => state.plan.parents.some(p => !state.appliedParents.includes(p.key) && p.occurrences.some(o => o.id === id && g.memberKeys.includes(o.primaryKey))))]));
  const selectedIds = new Set(Object.values(selectedOccurrences).flat());
  const activeGroups = groups.filter(g => selectedOccurrences[g.id].length > 0);
  const reviewedKeys = new Set(groups.flatMap(g => g.memberKeys));
  const equivalences = Object.fromEntries(activeGroups.map(g => {
    const entered = Object.entries(state.equivalences[g.id] ?? {}).filter(([key, value]) => g.memberKeys.includes(key) && value !== '');
    if (entered.length > 0 && entered.length !== g.memberKeys.length) invalidInput = true;
    return [g.id, Object.fromEntries(entered.map(([key, raw]) => [key, parse(raw)]))];
  }));
  const overrides = (values: Record<string, string>, waste = false) => Object.fromEntries(Object.entries(values).filter(([key, raw]) => selectedIds.has((JSON.parse(key) as string[])[0]) && (!state.selectedAlternatives || state.selectedAlternatives[JSON.parse(key)[0]]?.includes(JSON.parse(key)[1])) && raw !== '').map(([key, raw]) => [key, waste && raw === 'inherit' ? null : parse(raw, waste)]));
  const quantities = { ...state.quantities };
  if (state.selectedAlternatives) for (const parent of state.plan.parents) for (const occurrence of parent.occurrences) {
    if (!selectedIds.has(occurrence.id)) continue;
    for (const key of state.selectedAlternatives[occurrence.id] ?? []) {
      const id = overrideKey(occurrence.id, key);
      const product = state.plan.products.find(item => item.key === key);
      if (!product) continue;
      if (quantities[id] === undefined || quantities[id] === '') {
        const amount = defaultAlternativeQuantity(occurrence, product);
        if (amount === null || amount <= 0) invalidInput = true;
        else quantities[id] = String(amount);
      }
    }
  }
  const quantityOverrides = overrides(quantities) as Record<string, number>;
  const wasteOverrides = overrides(state.waste, true);
  return { invalidInput, request: {
    restaurantId: state.plan.restaurantId, historyWindow: state.plan.historyWindow, categoryFilter: state.plan.categoryFilter ?? 'all', groups,
    ...(state.plan.categories?.length ? { categories: state.plan.categories } : {}),
    ...(state.plan.selectedProductKeys ? { selectedProductKeys: state.plan.selectedProductKeys } : {}),
    selectedOccurrences, equivalences,
    ...(state.selectedAlternatives ? { selectedAlternatives: state.selectedAlternatives } : {}),
    quantityOverrides, wasteOverrides,
    expectedParents: Object.fromEntries(state.plan.parents.filter(p => !state.appliedParents.includes(p.key) && p.occurrences.some(o => selectedIds.has(o.id))).map(p => [p.key, p.fingerprint])),
    expectedCandidates: Object.fromEntries(state.plan.products.filter(p => reviewedKeys.has(p.key)).map(p => [p.key, p.fingerprint])),
  } };
}

/** Refresh is explicit: no choices or numeric approvals cross a changed snapshot. */
export function refreshAlternativeParents(state: AlternativeReviewState, fresh: AlternativeLinkPlan, keys: string[]): AlternativeReviewState {
  const freshProducts = new Map(fresh.products.map(product => [product.key, product]));
  const changedProducts = state.plan.products.filter(old => freshProducts.get(old.key)?.fingerprint !== old.fingerprint).map(p => p.key);
  const affectedIds = state.plan.parents.filter(p => keys.includes(p.key)).flatMap(p => p.occurrences.map(o => o.id));
  const affectedGroups = state.plan.groups.filter(g => (state.members[g.id] ?? g.memberKeys).some(k => changedProducts.includes(k))).map(g => g.id);
  const invalidIds = unique([...affectedIds, ...state.plan.groups.filter(group => affectedGroups.includes(group.id)).flatMap(group => groupOccurrences(state, group).map(row => row.occurrence.id))]);
  const keepOverride = (key: string) => !invalidIds.includes((JSON.parse(key) as string[])[0]);
  const refreshed = fresh.parents.filter(p => keys.includes(p.key));
  return { ...state, plan: { ...state.plan, products: fresh.products, parents: [...state.plan.parents.filter(p => !keys.includes(p.key)), ...refreshed] },
    ...(state.selectedAlternatives ? { selectedAlternatives: Object.fromEntries(Object.entries(state.selectedAlternatives).filter(([id]) => !invalidIds.includes(id))) } : {}),
    selected: Object.fromEntries(Object.entries(state.selected).map(([id, ids]) => [id, affectedGroups.includes(id) ? [] : ids.filter(o => !affectedIds.includes(o))])),
    equivalences: Object.fromEntries(Object.entries(state.equivalences).filter(([id]) => !affectedGroups.includes(id))),
    quantities: Object.fromEntries(Object.entries(state.quantities).filter(([key]) => keepOverride(key))),
    waste: Object.fromEntries(Object.entries(state.waste).filter(([key]) => keepOverride(key))),
    staleParents: state.staleParents.filter(k => !keys.includes(k)), staleCandidates: [],
  };
}

export function splitAlternativeCommit(parents: AlternativeLinkCommitParent[]): { batches: AlternativeLinkCommitParent[][]; oversized: AlternativeLinkCommitParent[] } {
  const batches: AlternativeLinkCommitParent[][] = [];
  const oversized: AlternativeLinkCommitParent[] = [];
  let batch: AlternativeLinkCommitParent[] = [];
  let links = 0;
  parents.forEach(parent => {
    if (parent.additions.length > 2000) { oversized.push(parent); return; }
    if (!parent.additions.length) return;
    if (batch.length === 100 || links + parent.additions.length > 2000) { batches.push(batch); batch = []; links = 0; }
    batch.push(parent); links += parent.additions.length;
  });
  if (batch.length) batches.push(batch);
  return { batches, oversized };
}

export function parentKeyFor(parents: AlternativeLinkParent[], target: { kind: string; id: string }): string | undefined {
  return parents.find(p => p.kind === target.kind && p.id === target.id)?.key;
}
